import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Loader2, X, Terminal } from 'lucide-react';

const TerminalOutput = ({ output, onClose }) => {
  const terminalRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to bottom when new content is added
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [output]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <div className="h-4 w-4 rounded-full border-2 border-gray-300" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'running':
        return 'text-blue-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Terminal className="h-5 w-5 mr-2" />
            {output.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Terminal Window */}
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-96 overflow-y-auto" ref={terminalRef}>
            <div className="mb-2 text-gray-400">
              <span className="text-red-400">$</span> nestology-delete --verbose
            </div>
            
            {output.operations.map((operation, index) => (
              <div key={index} className="flex items-center space-x-2 mb-1">
                {getStatusIcon(operation.status)}
                <span className={getStatusColor(operation.status)}>
                  {operation.name}
                </span>
                {operation.status === 'running' && (
                  <span className="text-gray-400 animate-pulse">...</span>
                )}
                {operation.status === 'completed' && (
                  <span className="text-green-400">✓</span>
                )}
                {operation.status === 'failed' && (
                  <span className="text-red-400">✗</span>
                )}
              </div>
            ))}

            {output.completed && (
              <div className="mt-4 pt-2 border-t border-gray-700">
                <div className="flex items-center space-x-2">
                  {output.success ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <span className={output.success ? 'text-green-400' : 'text-red-400'}>
                    {output.message}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {!output.completed && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${(output.operations.filter(op => op.status === 'completed').length / output.operations.length) * 100}%`
                }}
              ></div>
            </div>
          )}

          {/* Status Message */}
          {output.completed && (
            <div className={`p-4 rounded-lg ${
              output.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center">
                {output.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className={`font-medium ${
                  output.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {output.success ? 'Operation Completed Successfully' : 'Operation Failed'}
                </span>
              </div>
              <p className={`mt-1 text-sm ${
                output.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {output.message}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose} variant="outline">
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TerminalOutput;
